import api from '../lib/api';

const PRIMARY = [30, 41, 59];   // slate-800
const GRAY    = [100, 116, 139]; // slate-500
const LIGHT   = [241, 245, 249]; // slate-100
const BLACK   = [15, 23, 42];   // slate-900
const PAGE_W  = 210;
const PAGE_H  = 297;
const MARGIN  = 18;
const CONTENT_W = PAGE_W - MARGIN * 2;

const BILLING_PERIOD_LABELS = { monthly: 'Månadsvis', quarterly: 'Kvartalsvis', annual: 'Årsvis' };

function fmt(val) {
  const n = parseFloat(val) || 0;
  return n.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' SEK';
}

function fmtDate(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('sv-SE');
}

export async function downloadInvoicePdf(invoice) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  let company = {};
  try {
    const res = await api.get('/invoice-settings');
    company = res.data || {};
  } catch (_) { /* use empty */ }

  let y = MARGIN;

  // ── Header ────────────────────────────────────────────────────────────────
  // Left: company info
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...PRIMARY);
  doc.text(company.company_name || 'AIUS AB', MARGIN, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...GRAY);
  const companyLines = [
    company.address,
    company.email,
    company.phone,
    company.org_nr ? `Org.nr: ${company.org_nr}` : null,
    company.vat_nr ? `VAT: ${company.vat_nr}` : null,
  ].filter(Boolean);
  companyLines.forEach((line) => { doc.text(line, MARGIN, y); y += 4.5; });

  // Right: FAKTURA block
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...PRIMARY);
  doc.text('FAKTURA', PAGE_W - MARGIN, MARGIN + 2, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...GRAY);
  let ry = MARGIN + 12;
  const details = [
    ['Fakturanummer:', invoice.invoice_number || '–'],
    ['Fakturadatum:', fmtDate(invoice.created_at)],
    ['Förfallodatum:', fmtDate(invoice.due_date)],
    ['Fakturaperiod:', BILLING_PERIOD_LABELS[invoice.billing_period] || invoice.billing_period || '–'],
  ];
  details.forEach(([label, val]) => {
    doc.text(label, PAGE_W - MARGIN - 50, ry);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BLACK);
    doc.text(val, PAGE_W - MARGIN, ry, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY);
    ry += 5;
  });

  y = Math.max(y, ry) + 8;

  // ── Divider ───────────────────────────────────────────────────────────────
  doc.setDrawColor(...PRIMARY);
  doc.setLineWidth(0.4);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 7;

  // ── Recipient ─────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text('FAKTURERAS TILL', MARGIN, y);
  y += 5;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...BLACK);
  doc.text(invoice.org_name || '–', MARGIN, y);
  y += 10;

  // ── Description ───────────────────────────────────────────────────────────
  if (invoice.description) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...GRAY);
    doc.text(invoice.description, MARGIN, y, { maxWidth: CONTENT_W });
    y += 8;
  }

  // ── Lines table ───────────────────────────────────────────────────────────
  const COL = { desc: MARGIN, qty: MARGIN + 90, price: MARGIN + 115, amt: PAGE_W - MARGIN };
  const ROW_H = 7;

  // Header row
  doc.setFillColor(...PRIMARY);
  doc.rect(MARGIN, y, CONTENT_W, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text('Beskrivning', COL.desc + 2, y + 4.8);
  doc.text('Antal', COL.qty, y + 4.8, { align: 'center' });
  doc.text('À-pris', COL.price + 12, y + 4.8, { align: 'right' });
  doc.text('Belopp', COL.amt, y + 4.8, { align: 'right' });
  y += ROW_H;

  const lines = invoice.lines || [];
  lines.forEach((line, i) => {
    if (i % 2 === 0) {
      doc.setFillColor(...LIGHT);
      doc.rect(MARGIN, y, CONTENT_W, ROW_H, 'F');
    }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...BLACK);
    doc.text(line.description || '', COL.desc + 2, y + 4.8, { maxWidth: 82 });
    doc.text(String(line.quantity ?? 1), COL.qty, y + 4.8, { align: 'center' });
    doc.text(fmt(line.unit_price), COL.price + 12, y + 4.8, { align: 'right' });
    doc.text(fmt(line.amount), COL.amt, y + 4.8, { align: 'right' });
    y += ROW_H;
  });

  y += 4;

  // ── Totals ────────────────────────────────────────────────────────────────
  const totals = [
    ['Netto:', fmt(invoice.amount)],
    [`Moms (${invoice.vat_rate ?? 25}%):`, fmt(invoice.vat_amount)],
  ];
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  totals.forEach(([label, val]) => {
    doc.setTextColor(...GRAY);
    doc.text(label, PAGE_W - MARGIN - 48, y);
    doc.setTextColor(...BLACK);
    doc.text(val, PAGE_W - MARGIN, y, { align: 'right' });
    y += 5.5;
  });

  // Total row
  doc.setFillColor(...PRIMARY);
  doc.rect(PAGE_W - MARGIN - 54, y - 1, 54, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text('Totalt:', PAGE_W - MARGIN - 48, y + 4.2);
  doc.text(fmt(invoice.total_amount), PAGE_W - MARGIN, y + 4.2, { align: 'right' });
  y += 14;

  // ── Payment info ──────────────────────────────────────────────────────────
  doc.setDrawColor(...LIGHT);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text('BETALNINGSINFORMATION', MARGIN, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...BLACK);
  const payInfo = company.bankgiro
    ? [
        `Bankgiro: ${company.bankgiro}`,
        `Förfallodatum: ${fmtDate(invoice.due_date)}`,
        `Referens/OCR: ${invoice.payment_reference || invoice.invoice_number || '–'}`,
        company.email ? `Vid frågor: ${company.email}` : null,
      ].filter(Boolean)
    : [
        'Kontakta AIUS för betalningsinformation.',
        `Förfallodatum: ${fmtDate(invoice.due_date)}`,
        `Referens/OCR: ${invoice.payment_reference || invoice.invoice_number || '–'}`,
        company.email ? `Vid frågor: ${company.email}` : null,
      ].filter(Boolean);
  payInfo.forEach((line) => { doc.text(line, MARGIN, y); y += 5; });

  // ── Footer ────────────────────────────────────────────────────────────────
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY);
  doc.text(
    `${company.company_name || 'AIUS AB'} · ${company.org_nr || ''} · ${company.email || ''}`,
    PAGE_W / 2,
    PAGE_H - 10,
    { align: 'center' },
  );

  const fileName = `faktura_${(invoice.invoice_number || 'export').replace(/[^A-Za-z0-9-]/g, '_')}.pdf`;
  doc.save(fileName);
}
